<template>
    <div>
        <div class="row items-center">
            <div
                class="col-md-8 col-sm-12"
                :class="{ 'text-center': $q.screen.lt.md }"
            >
                <div class="text-h4 text-weight-medium">
                    {{ $t('Device Management') }}
                </div>
                <div class="page-description">
                    {{
                        $t(
                            'View the status of each device sending telemetry data to ioTORQ LEAN.'
                        )
                    }}
                </div>
            </div>
            <div
                v-if="!isLoading && devices.length > 0"
                class="col-md-4 col-sm-12 items-center"
                :class="$q.screen.lt.md ? 'text-center' : 'text-right'"
            >
                <span class="q-mr-md">
                    {{ $t('Last updated:') }}
                    {{ getLastUpdatedDate }}
                </span>
                <q-btn
                    class="bg-white"
                    icon="fas fa-sync"
                    color="accent"
                    @click="loadDevices"
                    outline
                    :disable="disableActions"
                >
                    <q-tooltip>{{ $t('Refresh') }}</q-tooltip>
                </q-btn>
            </div>
        </div>
        <!-- No facility selected banner -->
        <div class="col-12 q-mb-md">
            <q-banner
                v-if="showSelectBanner"
                class="q-pl-none bg-white border-grey q-pa-sm"
                rounded
                inline-actions
            >
                <div class="row items-center no-wrap">
                    <div>
                        <q-icon
                            name="fa fa-exclamation-circle"
                            color="primary"
                            size="md"
                            class="q-pa-sm q-mr-sm"
                        ></q-icon>
                    </div>
                    <div>
                        {{
                            $t(
                                'Please select a facility to show the list of devices sending data to ioTORQ LEAN'
                            )
                        }}
                    </div>
                </div>
            </q-banner>
        </div>
        <!-- Top row: Facility selector and filter buttons -->
        <div class="row q-mb-md justify-between">
            <div
                class="col-md-3 col-xs-12"
                :class="$q.screen.lt.md ? 'q-mb-md' : ''"
            >
                <q-select
                    v-model="dropdownOption"
                    :options="getFacilitiesOptions"
                    :label="$t('Facility')"
                    :clearable="true"
                    :dense="true"
                    :outlined="true"
                    :emit-value="false"
                    :disable="isLoading"
                    @input="loadDevices"
                    bg-color="white"
                    color="accent"
                />
            </div>
            <div class="col-md-6 col-xs-12">
                <div
                    class="row action-row q-gutter-sm"
                    :class="$q.screen.lt.md ? 'justify-center' : 'justify-end'"
                >
                    <q-btn-group outline>
                        <q-btn
                            @click="selectStatus('all')"
                            :text-color="
                                selectedStatus === 'all' ? 'white' : 'accent'
                            "
                            color="accent"
                            :disable="disableActions"
                            :outline="selectedStatus !== 'all'"
                        >
                            {{ $t('All') }} ({{ getAllDevicesCount }})
                            <q-tooltip>
                                {{ $t('Show All Devices') }}
                            </q-tooltip>
                        </q-btn>
                        <q-btn
                            @click="selectStatus('online')"
                            :text-color="
                                selectedStatus === 'online' ? 'white' : 'accent'
                            "
                            color="accent"
                            :disable="disableActions"
                            :outline="selectedStatus !== 'online'"
                        >
                            {{ $t('Online') }} ({{ getOnlineDevicesCount }})
                            <q-tooltip>
                                {{ $t('Show Online Devices') }}
                            </q-tooltip>
                        </q-btn>
                        <q-btn
                            @click="selectStatus('offline')"
                            :text-color="
                                selectedStatus === 'offline'
                                    ? 'white'
                                    : 'accent'
                            "
                            color="accent"
                            :disable="disableActions"
                            :outline="selectedStatus !== 'offline'"
                        >
                            {{ $t('Offline') }} ({{ getOfflineDevicesCount }})
                            <q-tooltip>
                                {{ $t('Show Offline Devices') }}
                            </q-tooltip>
                        </q-btn>
                    </q-btn-group>
                    <!-- Notification button starts here -->
                    <q-btn-dropdown
                        ref="deviceDisconnectionNotifications"
                        color="accent"
                        :label="$t('Notifications')"
                        :disable="disableActions"
                        :menu-anchor="
                            $q.screen.lt.md ? 'bottom middle' : 'bottom left'
                        "
                        :menu-self="$q.screen.lt.md ? 'top middle' : 'top left'"
                        outline
                    >
                        <q-card style="min-width: 300px">
                            <q-card-section class="q-pb-md">
                                <div
                                    class="text-grey-8 text-weight-medium text-center"
                                >
                                    {{ $t('Get Notified of Disconnections') }}
                                </div>
                            </q-card-section>

                            <q-separator />
                            <q-card-section class="q-pa-none">
                                <q-virtual-scroll :items="devices">
                                    <template v-slot:before>
                                        <q-item>
                                            <q-item-section>
                                                <q-item-label
                                                    lines="1"
                                                    class="text-weight-bold"
                                                >
                                                    {{ $t('Device') }}
                                                </q-item-label>
                                            </q-item-section>
                                            <q-item-section side>
                                                <q-item-label>
                                                    <q-item-label
                                                        lines="1"
                                                        class="text-weight-bold text-black"
                                                    >
                                                        {{ $t('Enable') }}
                                                    </q-item-label>
                                                </q-item-label>
                                            </q-item-section>
                                        </q-item>
                                    </template>
                                    <template v-slot="{ item, index }">
                                        <q-item :key="index" dense>
                                            <q-item-section
                                                @click="
                                                    toggleDeviceNotificationPreference(
                                                        index
                                                    )
                                                "
                                                class="cursor-pointer"
                                            >
                                                <q-item-label lines="1">
                                                    {{ item.serialNumber }}
                                                </q-item-label>
                                            </q-item-section>
                                            <q-item-section side>
                                                <q-item-label>
                                                    <q-toggle
                                                        :model-value="
                                                            item.notifications
                                                                .isNotified
                                                        "
                                                        color="accent"
                                                        dense
                                                        size="md"
                                                        @update:model-value="
                                                            toggleDeviceNotificationPreference(
                                                                index
                                                            )
                                                        "
                                                    ></q-toggle>
                                                </q-item-label>
                                            </q-item-section>
                                        </q-item>
                                    </template>
                                </q-virtual-scroll>
                            </q-card-section>
                            <q-card-actions class="row justify-between q-py-md">
                                <div>
                                    <q-btn
                                        @click="selectAllDevices"
                                        :label="$t('Select All')"
                                        class="q-mr-sm"
                                        text-color="primary"
                                        color="white"
                                        flat
                                    />

                                    <q-btn
                                        @click="clearAllDevices"
                                        :label="$t('Clear')"
                                        text-color="primary"
                                        color="white"
                                        flat
                                    />
                                </div>
                                <div>
                                    <q-btn
                                        @click="cancelNotificationPreferences"
                                        :label="$t('Cancel')"
                                        class="q-mr-sm"
                                        text-color="primary"
                                        color="white"
                                        outline
                                    />
                                    <q-btn
                                        :label="$t('Save')"
                                        type="submit"
                                        color="primary"
                                        id="save-button"
                                        @click="saveNotificationPreferences"
                                        :disable="
                                            deviceNotificationsPreferences
                                                .subscribe_to.length === 0 &&
                                            deviceNotificationsPreferences
                                                .unsubscribe_from.length === 0
                                        "
                                    />
                                </div>
                            </q-card-actions>
                        </q-card>
                    </q-btn-dropdown>
                    <!-- Notification button ends here -->
                </div>
            </div>
        </div>
        <div
            v-if="isLoading"
            class="col-md-6 col-xs-12 text-center"
            style="height: 100vh"
        >
            <q-spinner
                style="margin-top: 250px"
                color="primary"
                size="3em"
                :thickness="10"
            />
            <p class="q-mt-md">
                {{ `${$t('Loading devices data')}...` }}
            </p>
        </div>
        <!-- Device management cards -->
        <div
            v-if="devices.length > 0 && !isLoading"
            class="row q-col-gutter-md items-center q-pt-md"
        >
            <div
                class="col-xs-12 col-sm-6 col-md-4 col-lg-3 col-xl-2"
                v-for="(device, index) in getDevicesByStatus(selectedStatus)"
                :key="device.id"
            >
                <device-management-card
                    :key="index"
                    :id="device.id"
                    :assetsCount="device.assets.length"
                    :serialNumber="device.serialNumber"
                    :uptime="getDisconnectionsReport(device.id).uptime"
                    :isOffline="getDisconnectionsReport(device.id).isOffline"
                    :disconnectionTimeline="
                        getDisconnectionsReport(device.id).timeline
                    "
                    :totalDurationInSeconds="
                        getDisconnectionsReport(device.id)
                            .totalDurationInSeconds
                    "
                    :totalDisconnectionsCount="
                        getDisconnectionsReport(device.id)
                            .totalDisconnectionsCount
                    "
                    :isNotified="device.notifications.isNotified"
                    @cardClick="openDeviceDetail"
                    @buttonClick="openLinkedAssets"
                />
            </div>
        </div>
        <div
            v-if="devices.length === 0 && !isLoading && !showSelectBanner"
            class="full-width column flex-center q-pa-lg q-gutter-sm"
            :class="$q.screen.lt.md ? 'border' : ''"
        >
            <q-icon
                name="fa-solid fa-router"
                class="icon-bg"
                color="primary"
                size="md"
            />

            <div class="text-h6 text-center">
                {{ $t('No device has been found.') }}
            </div>
            <div class="text-subtitle1 text-secondary">
                {{
                    $t(
                        'Please contact support to add a device to the {facility} facility.',
                        {
                            facility: selectedFacility.name
                        }
                    )
                }}
            </div>
            <a href="mailto:support@iotorq-lean.com" class="q-mt-md">
                <q-btn color="primary" :label="$t('Report an issue')"> </q-btn>
            </a>
        </div>

        <!-- Dialogs -->
        <!-- Device details dialog -->
        <device-details-dialog
            v-if="showDeviceDetailsDialog"
            :facilityId="selectedFacility.id"
            :device="selectedDevice"
            :start="getReportPeriodISOStr().start"
            :end="getReportPeriodISOStr().end"
            @close="showDeviceDetailsDialog = false"
        />

        <!-- Linked assets dialog -->
        <device-linked-assets-dialog
            v-if="showDeviceLinkedAssetsDialog"
            :assets="selectedDevice.assets"
            :facilityId="selectedFacility.id"
            :serialNumber="selectedDevice.serialNumber"
            :show="showDeviceLinkedAssetsDialog"
            @close="showDeviceLinkedAssetsDialog = false"
        />
    </div>
</template>
<script>
import BreadcrumbsManager from '@mixins/BreadcrumbsManager.vue';
import DeviceDetailsDialog from './DeviceDetailsDialog.vue';
import DeviceLinkedAssetsDialog from './DeviceLinkedAssetsDialog.vue';
import DeviceManagementCard from './DeviceManagementCard.vue';
import DeviceManager from './deviceManager.js';
import { mapActions, mapState } from 'pinia';
import { useAuthStore } from '@stores/auth';
import { useFacilitiesStore } from '@stores/facilities.ts';

export default {
    name: 'DeviceManagement',
    mixins: [BreadcrumbsManager],
    components: {
        DeviceDetailsDialog,
        DeviceManagementCard,
        DeviceLinkedAssetsDialog
    },
    mounted() {
        // Set breadcrumbs
        if (
            !this.selectedFacility ||
            !this.selectedFacility.id ||
            !this.selectedFacility.name
        ) {
            this.setBreadcrumbs([
                {
                    label: this.$t('Facilities'),
                    route: {
                        name: 'facility_list'
                    }
                },
                {
                    label: this.$t('Global Settings'),
                    route: {
                        name: 'settings'
                    }
                },
                {
                    label: this.$t('Device Management')
                }
            ]);
        } else {
            this.setBreadcrumbs([
                {
                    label: this.$t('Facilities'),
                    route: {
                        name: 'facility_list'
                    }
                },
                {
                    label: this.selectedFacility.name,
                    route: {
                        name: 'facility_dashboard',
                        params: {
                            facilityId: this.selectedFacility.id
                        }
                    }
                },
                {
                    label: this.$t('Settings'),
                    route: {
                        name: 'facility_settings',
                        params: {
                            facilityId: this.selectedFacility.id
                        }
                    }
                },
                {
                    label: this.$t('Device Management')
                }
            ]);
        }

        this.preselectFacilityIfSingle();

        this.deviceManager = new DeviceManager(this.localTimezone, this.userId);
        this.loadDevices();
    },
    data() {
        return {
            reportIntervalDays: 30,

            // loading animation
            isLoading: false,

            devices: [],
            disconnectionsReport: new Map(),
            lastUpdatedDate: '',

            selectedStatus: 'all',
            selectedDevice: [],
            deviceNotificationsPreferences: {
                subscribe_to: [],
                unsubscribe_from: []
            },
            showDeviceDetailsDialog: false,
            showDeviceLinkedAssetsDialog: false,

            localTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone
        };
    },
    watch: {
        selectedFacility() {
            this.loadDevices();
        }
    },
    computed: {
        ...mapState(useFacilitiesStore, [
            'getFacilities',
            'getSelectedFacility'
        ]),
        ...mapState(useAuthStore, {
            userLocale: 'getUserLocale',
            user: 'user'
        }),
        selectedFacility() {
            return this.getSelectedFacility;
        },
        dropdownOption: {
            // when selected facility in store changes, update the selected option in the dropdown
            get() {
                if (this.selectedFacility) {
                    return {
                        label: this.selectedFacility['name'],
                        value: this.selectedFacility
                    };
                }
                return null;
            },
            // when changing the option selected in the dropdown, update selected facility in store
            set(selected) {
                if (selected) {
                    this.selectFacilityAction(selected['value']);
                } else {
                    this.clearSelectedFacilityAction();
                }
            }
        },
        userId() {
            return this.user.id;
        },
        /**
         * Checks if loading or no facility selected.
         * @return {Boolean} - True if loading or no facility selected.
         */
        disableActions() {
            return this.isLoading || !this.selectedFacility;
        },
        /**
         * Controls banner visibility
         * @return {Boolean} - Show banner if there is no facility selected.
         */
        showSelectBanner() {
            return !this.selectedFacility;
        },
        /**
         * Get the total count of devices.
         * @return {Number}
         */
        getAllDevicesCount() {
            return this.getDevicesByStatus('all').length;
        },
        /**
         * Get the total count of online devices.
         * @return {Number}
         */
        getOnlineDevicesCount() {
            return this.getDevicesByStatus('online').length;
        },
        /**
         * Get the total count of offline devices.
         * @return {Number}
         */
        getOfflineDevicesCount() {
            return this.getDevicesByStatus('offline').length;
        },
        /**
         * Get facilities options for the select component.
         * @return {Object[]} - Array of facilities.
         */
        getFacilitiesOptions() {
            const facilitiesList = [];
            const facilities = this.getFacilities;
            // facilities can be null if the user has not finished loadFacilities yet
            if (facilities) {
                facilities.map(facility =>
                    facilitiesList.push({
                        label: facility.name,
                        value: parseInt(facility.id)
                    })
                );
            }
            return facilitiesList;
        },
        /**
         * Get the report last updated date.
         * @return {String}
         */
        getLastUpdatedDate() {
            return new Date(this.lastUpdatedDate).toLocaleString(
                this.userLocale,
                {
                    month: 'short',
                    day: '2-digit',
                    hour: 'numeric',
                    minute: '2-digit'
                }
            );
        },
        /**
         * Get default report data object.
         * @return {Object} - Default report data object with no disconnections
         */
        defaultReportData() {
            const { start, end } = this.getReportPeriodISOStr();
            return this.deviceManager.generateDefaultReportData(start, end);
        }
    },
    methods: {
        ...mapActions(useFacilitiesStore, [
            'selectFacilityAction',
            'clearSelectedFacilityAction'
        ]),
        /**
         * Get a period of the past 30 days from the current date
         * @return {Object} - An object with start and end dates as String in ISO 8601 format
         */
        getReportPeriodISOStr() {
            // Ending date is the current date
            const end = new Date();
            // Starting date is previous report days excluding the current day
            const start = new Date(
                new Date().setDate(
                    end.getDate() - (this.reportIntervalDays - 1)
                )
            );
            return {
                start: start.toISOString(),
                end: end.toISOString()
            };
        },
        /**
         * Get a device object by id
         * @param {Number} deviceId - The device id
         * @return {Object} - Device object
         */
        getDevice(deviceId) {
            return this.devices.find(device => {
                return device['id'] === deviceId;
            });
        },
        /**
         * Get devices by status
         * @param {String} status
         * @return {[]} - An array of filtered devices.
         */
        getDevicesByStatus(status = 'all') {
            if (status === 'offline') {
                return this.devices.filter(device => {
                    const deviceReportData = this.disconnectionsReport.get(
                        device.id
                    );
                    if (
                        deviceReportData &&
                        typeof deviceReportData === 'object' &&
                        'isOffline' in deviceReportData &&
                        deviceReportData.isOffline
                    ) {
                        return true;
                    }
                    return false;
                });
            } else if (status === 'online') {
                return this.devices.filter(device => {
                    const deviceReportData = this.disconnectionsReport.get(
                        device.id
                    );
                    if (
                        !deviceReportData ||
                        (typeof deviceReportData === 'object' &&
                            'isOffline' in deviceReportData &&
                            !deviceReportData.isOffline)
                    ) {
                        return true;
                    }
                    return false;
                });
            } else {
                return this.devices;
            }
        },
        /**
         * Open device details dialog for a chosen device
         */
        openDeviceDetail(id) {
            this.selectedDevice = this.getDevice(id);
            this.showDeviceDetailsDialog = true;
        },
        /**
         * Open linked assets dialog for a chosen device
         */
        openLinkedAssets(id) {
            this.selectedDevice = this.getDevice(id);
            this.showDeviceLinkedAssetsDialog = true;
        },
        /**
         * Update the status used to filter displayed devices
         */
        selectStatus(status) {
            this.selectedStatus = status;
        },
        /**
         * Load devices and disconnections for a given facility.
         */
        async loadDevices() {
            if (!this.selectedFacility) {
                this.devices = [];
                return;
            }

            this.isLoading = true;
            this.selectStatus('all');

            try {
                this.devices = [];

                // Get period of 30 days
                const { start, end } = this.getReportPeriodISOStr();

                // Get devices data
                this.devices = await this.deviceManager.getAllDevices(
                    this.selectedFacility.id
                );

                // Get Disconnections report data
                const disconnectionsReportData =
                    await this.deviceManager.getDisconnectionsReport(
                        this.selectedFacility.id,
                        start,
                        end
                    );

                // set disconnections report data
                this.disconnectionsReport = disconnectionsReportData['report'];
                this.lastUpdatedDate = disconnectionsReportData['end'];
            } catch (error) {
                console.error('Failed to fetch devices data due to: ', error);
            } finally {
                this.isLoading = false;
            }
        },
        /*
         * Get disconnections report data for a given device by checking the map
         * The API endpoint only returns data for devices with disconnections
         * If not found, generate report data with zero disconnections for the device
         */
        getDisconnectionsReport(deviceId) {
            // check if the device has disconnections
            if (this.disconnectionsReport.has(deviceId)) {
                return this.disconnectionsReport.get(deviceId);
            }

            // If not, generate default report data with no disconnections
            const reportData = this.defaultReportData;
            // set device id
            reportData['id'] = deviceId;
            return reportData;
        },
        /**
         * Takes the deviceNotificationPayload and makes
         * a backend API call to save the user's changed
         * notification preferences.
         * Closes the notification dropdown in the end.
         */
        async saveNotificationPreferences() {
            try {
                const response = await this.$axios.post(
                    '/devices/bulk_toggle_notifications/',
                    this.deviceNotificationsPreferences
                );
                if (response.status === 200) {
                    this.$notifySuccess(
                        this.$t(
                            'Disconnection notification preferences saved successfully.'
                        )
                    );
                    this.loadDevices();
                } else {
                    this.$notifyError(
                        this.$t(
                            'Failed to save disconnection notification preferences.'
                        )
                    );
                }
            } catch (error) {
                this.$notifyError(
                    this.$t(
                        'Failed to save disconnection notification preferences.',
                        error
                    )
                );
            } finally {
                this.resetDeviceNotificationsPreferences();
                this.$refs.deviceDisconnectionNotifications.hide();
            }
        },
        /**
         * Cancel editing the device notification dropdown menu and
         * return the device notification choices to its original form.
         */
        cancelNotificationPreferences() {
            this.devices.forEach(device => {
                device['notifications']['isNotified'] =
                    device['notifications']['originalNotificationStatus'];
            });
            this.resetDeviceNotificationsPreferences();
            this.$refs.deviceDisconnectionNotifications.hide();
        },
        /**
         * Toggle the status of the device notifications and insert that
         * device in notification http payload.
         * @param {Integer} deviceIndex index of the device for which the
         * notification is being toggled.
         */
        toggleDeviceNotificationPreference(deviceIndex) {
            const device = this.devices[deviceIndex];
            // toggle the boolean value so that the toggle
            // icon can display the user's current choice
            device.notifications.isNotified = !device.notifications.isNotified;
            // if the user's current choice is different than what
            // we receive from the backend API call, only then insert
            // that change in the http payload(subscribe/unsubscribe), otherwise filter out
            // that device id from the payload(subscribe/unsubscribe), if exists.
            if (
                device.notifications.originalNotificationStatus === false &&
                device.notifications.isNotified === true
            ) {
                this.deviceNotificationsPreferences.subscribe_to.push(
                    device.id
                );
            } else if (
                device.notifications.originalNotificationStatus === true &&
                device.notifications.isNotified === false
            ) {
                this.deviceNotificationsPreferences.unsubscribe_from.push(
                    device.id
                );
            } else {
                this.deviceNotificationsPreferences.subscribe_to =
                    this.deviceNotificationsPreferences.subscribe_to.filter(
                        deviceId => deviceId != device.id
                    );
                this.deviceNotificationsPreferences.unsubscribe_from =
                    this.deviceNotificationsPreferences.unsubscribe_from.filter(
                        deviceId => deviceId != device.id
                    );
            }
        },
        resetDeviceNotificationsPreferences() {
            this.deviceNotificationsPreferences.subscribe_to = [];
            this.deviceNotificationsPreferences.unsubscribe_from = [];
        },
        /**
         * Select all devices
         * The toggleDeviceNotificationPreference inverts the
         * isNotified property of each device. Therefore, this method
         * sets the isNotified property of each device to false and
         * then calls the toggleDeviceNotificationPreference method to
         * update the notification preferences.
         */
        selectAllDevices() {
            this.devices.forEach((device, index) => {
                device.notifications.isNotified = false;
                this.toggleDeviceNotificationPreference(index);
            });
        },
        /**
         * clear selection from all devices
         * The toggleDeviceNotificationPreference inverts the
         * isNotified property of each device. Therefore, this method
         * sets the isNotified property of each device to true and
         * then calls the toggleDeviceNotificationPreference method to
         * update the notification preferences.
         */
        clearAllDevices() {
            this.devices.forEach((device, index) => {
                device.notifications.isNotified = true;
                this.toggleDeviceNotificationPreference(index);
            });
        },
        /**
         * Preselect facility if only one facility is available
         */
        preselectFacilityIfSingle() {
            if (this.getFacilitiesOptions.length === 1) {
                this.dropdownOption = this.getFacilitiesOptions[0];
            }
        }
    }
};
</script>
